/**
 * Mirrors Crm::AttributionExtractor on the client so a conversation row can
 * name the ad it came from without a second request. The backend already
 * persists the extracted values onto the opportunity, but a conversation has
 * no direct association to one, so surfacing it that way would cost a query
 * per row on the hottest screen in the product. The referral payload is
 * already present in what the list endpoint returns, so it is read from there.
 *
 * Key lists are kept byte-identical to the Ruby constants. If those change,
 * these must change with them.
 */
const REFERRAL_KEYS = ['referral', 'whatsapp_referral', 'meta_referral'];
const AD_ID_KEYS = ['ad_id', 'source_id'];
const HEADLINE_KEYS = ['headline', 'ad_headline', 'title'];

const firstValue = (payload, keys) => {
  if (!payload) return '';
  const hit = keys
    .map(key => payload[key])
    .filter(value => value !== null && value !== undefined)
    .map(value => String(value).trim())
    .find(value => value.length > 0);
  return hit || '';
};

const isPlainObject = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const referralIn = payload => {
  if (!isPlainObject(payload)) return null;

  const nested = REFERRAL_KEYS.map(key => payload[key]).find(isPlainObject);
  if (nested) return nested;

  // Some adapters flatten the referral onto the payload itself rather than
  // nesting it, so a payload that carries an ad id directly still counts.
  return firstValue(payload, [...AD_ID_KEYS, ...HEADLINE_KEYS])
    ? payload
    : null;
};

const referralFrom = payloads => payloads.map(referralIn).find(Boolean) || null;

/**
 * @returns {{adId: string, headline: string, label: string}|null}
 *   `null` when the conversation carries no usable referral. A referral that
 *   is present but incomplete still returns an object, because partial
 *   evidence is worth showing and must never suppress the row.
 */
export const conversationCampaignOrigin = conversation => {
  if (!conversation) return null;

  const lastMessage =
    conversation.last_non_activity_message ||
    (Array.isArray(conversation.messages) ? conversation.messages[0] : null);

  const referral = referralFrom([
    conversation.additional_attributes,
    conversation.custom_attributes,
    lastMessage?.content_attributes,
    lastMessage?.additional_attributes,
  ]);

  if (!referral) return null;

  const adId = firstValue(referral, AD_ID_KEYS);
  const headline = firstValue(referral, HEADLINE_KEYS);
  if (!adId && !headline) return null;

  return { adId, headline, label: headline || adId };
};
